'use client'
import React, { useState, useEffect } from 'react'
import Link from 'next/link'
import { useTheme, ThemeToggle } from '@/components/ThemeProvider'
import { createClient } from '@/lib/supabase/client'

export default function Navbar({ activePage }: { activePage?: 'home' | 'marketplace' | 'agent' }) {
  const { theme } = useTheme()
  const [user, setUser] = useState<any>(null)
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false)
  const [logoConfig, setLogoConfig] = useState<any>(null)
  const [logoReady, setLogoReady] = useState(false)
  const [mounted, setMounted] = useState(false)

  useEffect(() => {
    setMounted(true)
    const supabase = createClient()
    supabase.auth.getUser().then(({ data }) => {
      setUser(data?.user ?? null)
    })

    fetch('/api/site-content')
      .then(r => r.json())
      .then(d => { setLogoConfig(d?._logo || null); setLogoReady(true) })
      .catch(() => setLogoReady(true))
  }, [])

  if (!mounted) return (
    <nav className="fixed top-0 w-full bg-[#FAFAF8]/90 backdrop-blur-xl border-b border-[#E8E8E4] z-50 h-11 sm:h-14" />
  )

  const navLogo = theme === 'dark'
    ? (logoConfig?.whiteUrl || '/logo-white.png')
    : (logoConfig?.darkUrl || '/logo-dark.png')
  const headerPx = logoConfig?.headerSize || 20
  const headerPxMobile = Math.round(headerPx * 0.8)

  const linkClass = (page?: string) =>
    `text-sm transition ${activePage === page ? 'text-[#1a1a1a] dark:text-white font-medium' : 'text-gray-500 dark:text-gray-400 hover:text-[#1a1a1a] dark:hover:text-white'}`

  const mobileLinkClass = (page?: string) =>
    `py-2.5 text-sm transition ${activePage === page ? 'text-[#1a1a1a] dark:text-white font-medium' : 'text-gray-600 dark:text-gray-300 hover:text-[#1a1a1a] dark:hover:text-white'}`

  return (
    <nav className="fixed top-0 w-full bg-[#FAFAF8]/90 dark:bg-gray-950/90 backdrop-blur-xl border-b border-[#E8E8E4] dark:border-gray-800/50 z-50">
      <div className="max-w-7xl mx-auto px-3 sm:px-6 h-11 sm:h-14 flex items-center justify-between">

        {/* Logo */}
        <Link href="/">
          {/* Mobile: 80% size, Desktop: full size */}
          <img
            src={navLogo}
            alt="Preplix"
            className={`transition-opacity duration-300 md:hidden ${logoReady ? 'opacity-100' : 'opacity-0'}`}
            style={{ height: `${headerPxMobile}px` }}
          />
          <img
            src={navLogo}
            alt="Preplix"
            className={`transition-opacity duration-300 hidden md:block ${logoReady ? 'opacity-100' : 'opacity-0'}`}
            style={{ height: `${headerPx}px` }}
          />
        </Link>

        {/* Desktop nav */}
        <div className="hidden md:flex items-center gap-8">
          <Link href="/#department" className={linkClass('home')}>AI Department</Link>
          <Link href="/#how-it-works" className={linkClass()}>How It Works</Link>
          <Link href="/#pricing" className={linkClass()}>Pricing</Link>
          <Link href="/ai-employee-marketplace" className={linkClass('marketplace')}>Marketplace</Link>
          <Link href="/ai-agent" className={linkClass('agent')}>Single Agent</Link>
          <Link href="/squidclaw" className={linkClass('squidclaw')} style={{ color: '#7c3aed', fontWeight: 600 }}>Squidclaw</Link>
          <ThemeToggle />
          {user ? (
            <Link href="/account" className="inline-flex items-center bg-[#1a1a1a] dark:bg-white text-white dark:text-[#1a1a1a] text-sm px-5 py-2 rounded-full hover:bg-black dark:hover:bg-gray-200 transition font-medium">
              My Account
            </Link>
          ) : (
            <Link href="/login" className="inline-flex items-center bg-[#1a1a1a] dark:bg-white text-white dark:text-[#1a1a1a] text-sm px-5 py-2 rounded-full hover:bg-black dark:hover:bg-gray-200 transition font-medium">
              Sign In
            </Link>
          )}
        </div>

        {/* Mobile: theme toggle + hamburger */}
        <div className="md:hidden flex items-center gap-2">
          <ThemeToggle />
          <button
            onClick={() => setMobileMenuOpen(o => !o)}
            className="p-2 rounded-lg text-gray-600 dark:text-gray-300 hover:bg-gray-100 dark:hover:bg-gray-800 transition"
            aria-label="Menu"
          >
            {mobileMenuOpen ? (
              <svg xmlns="http://www.w3.org/2000/svg" width="20" height="20" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" viewBox="0 0 24 24"><line x1="18" y1="6" x2="6" y2="18"/><line x1="6" y1="6" x2="18" y2="18"/></svg>
            ) : (
              <svg xmlns="http://www.w3.org/2000/svg" width="20" height="20" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" viewBox="0 0 24 24"><line x1="3" y1="6" x2="21" y2="6"/><line x1="3" y1="12" x2="21" y2="12"/><line x1="3" y1="18" x2="21" y2="18"/></svg>
            )}
          </button>
        </div>
      </div>

      {/* Mobile dropdown */}
      {mobileMenuOpen && (
        <div className="md:hidden border-t border-[#E8E8E4] dark:border-gray-800 bg-[#FAFAF8]/95 dark:bg-gray-950/95 backdrop-blur-xl px-4 py-3 flex flex-col gap-1">
          <Link href="/#department" onClick={() => setMobileMenuOpen(false)} className={mobileLinkClass('home')}>AI Department</Link>
          <Link href="/#how-it-works" onClick={() => setMobileMenuOpen(false)} className={mobileLinkClass()}>How It Works</Link>
          <Link href="/#pricing" onClick={() => setMobileMenuOpen(false)} className={mobileLinkClass()}>Pricing</Link>
          <Link href="/ai-employee-marketplace" onClick={() => setMobileMenuOpen(false)} className={mobileLinkClass('marketplace')}>Marketplace</Link>
          <Link href="/ai-agent" onClick={() => setMobileMenuOpen(false)} className={mobileLinkClass('agent')}>Single Agent</Link>
          <Link href="/squidclaw" onClick={() => setMobileMenuOpen(false)} className={mobileLinkClass('squidclaw')} style={{ color: '#7c3aed', fontWeight: 600 }}>Squidclaw</Link>
          <div className="pt-2 border-t border-[#E8E8E4] dark:border-gray-800 flex flex-col gap-2">
            {user ? (
              <Link href="/account" onClick={() => setMobileMenuOpen(false)} className="block w-full text-center bg-[#1a1a1a] dark:bg-white text-white dark:text-[#1a1a1a] text-sm px-5 py-2.5 rounded-full font-medium">My Account</Link>
            ) : (
              <Link href="/login" onClick={() => setMobileMenuOpen(false)} className="block w-full text-center bg-[#1a1a1a] dark:bg-white text-white dark:text-[#1a1a1a] text-sm px-5 py-2.5 rounded-full font-medium">Sign In</Link>
            )}
          </div>
        </div>
      )}
    </nav>
  )
}
